import { ParsedUrlQuery } from "querystring";

import { useRef } from "react";

import {
  Box,
  Button,
  Card,
  Collapse,
  Group,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronUp, IconInfoCircle } from "@tabler/icons-react";
import _ from "lodash";
import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import PDFDocument from "pdfkit";

import { getFullWidthLayout } from "components/layouts/fullWidth";
import { PageContentBox } from "components/layouts/PageContentBox";
import { LevelComparisonTable } from "components/LevelComparisonTable";
import { PercentileSelector } from "components/PercentileSelector";
import { SampleSizeBadge } from "components/SampleSizeBadge";
import { SearchByBar } from "components/SearchByBar";
import { SelectedPercentileProvider } from "components/SelectedPercentileProvider";
import { MarketRangeCard } from "components/TitlePage/MarketRangeCard";
import { SearchSidebar } from "components/TitlePage/SearchSidebar";
import { TitlePageResultsCard } from "components/TitlePage/TitlePageResultsCard";
import { descriptions } from "constants/levelData";
import { getSafeExplorerSearchQueryString, useSearchRouteInfo } from "hooks/routing";
import { benchmarkingPrisma } from "lib/benchmarkingPrisma";
import { FamilyLevelData, TitleExplorerCompanyRollup } from "lib/frontend/benchmarking/types";
import { DataComparison } from "prisma/benchmarking/client";
import { JobTitleService, SimilarTitleResult } from "services/JobTitleService";
import { PostService, StatsSummary } from "services/PostsService";
import { formatRange, levelLabel, searchParamsToDictionary } from "utils/utils";

const PAGE_CONTENT_MAX_WIDTH = 1200;

interface PageProps {
  title?: string;
  titleCardStats?: StatsSummary;
  dataComparison?: DataComparison | null;
  similarTitles?: SimilarTitleResult[];
  results?: TitleExplorerCompanyRollup[];
  numTotalResults?: number;
  numCompanies?: number;
  numUniqueRanges?: number;
  percentiles?: StatsSummary["percentiles"];
  overallRangeMin?: number;
  overallRangeMax?: number;
  offset?: number;
  numPosts?: number;
  numRanges?: number;
  stats?: StatsSummary;
  familyLevelData?: FamilyLevelData[] | null;
  mode?: "family" | "title";
  error?: string;
}

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>,
) => {
  const { params, query, res } = context;
  const rawQuery = params?.query?.[0];
  const isPdfRequested = query.format === "pdf";

  const queryString = getSafeExplorerSearchQueryString(rawQuery);
  const searchParams = new URLSearchParams(queryString);
  const queryArgs = searchParamsToDictionary(searchParams);

  // Check if PDF format is requested via query string

  let mode: undefined | "family" | "title";

  const title = queryArgs.title[0];

  if (title) {
    mode = "title";
  } else if (queryArgs.jobFamily?.[0] && queryArgs.level?.[0]) {
    mode = "family";
  } else {
    return {
      notFound: true,
    };
  }

  if (mode === "title") {
    if (!title || typeof title !== "string") {
      return {
        notFound: true,
      };
    }
  } else if (mode === "family") {
    const { jobFamily, level } = queryArgs;

    if (!jobFamily || !level) {
      return {
        notFound: true,
      };
    }
  } else {
    return {
      notFound: true,
    };
  }

  const postService = new PostService(benchmarkingPrisma);
  const jobTitleService = new JobTitleService(benchmarkingPrisma);

  let tableData: FamilyLevelData[] | null = null;

  if (mode === "family") {
    tableData = await postService.getFamilyDataByLevel({
      ...queryArgs,
      jobFamily: [queryArgs.jobFamily[0]],
    });
  }

  const [dataComparison, { results, stats }] = await Promise.all([
    jobTitleService.getDataComparison(title),
    postService.getTitlePageData(queryArgs, mode),
  ]);

  let pageHeader = title;
  if (mode === "family") {
    if (queryArgs.level) {
      pageHeader = `${queryArgs.jobFamily[0]} - ${queryArgs.level[0]}`;
    } else {
      pageHeader = `${queryArgs.jobFamily[0]}`;
    }
  }

  const props: PageProps = {
    title: pageHeader,
    dataComparison,
    results,
    numTotalResults: stats.numCompanies,
    numCompanies: stats.numCompanies,
    percentiles: stats.percentiles,
    overallRangeMin: stats.absoluteMin,
    overallRangeMax: stats.absoluteMax,
    numPosts: stats.numPosts,
    numRanges: stats.numRanges,
    numUniqueRanges: stats.numUniqueRanges,
    offset: queryArgs.offset ? _.toNumber(queryArgs.offset) : 0,
    stats: stats,
    familyLevelData: tableData,
    mode,
  };

  // If PDF format is requested, generate and return PDF
  if (isPdfRequested && res) {
    try {
      // Create PDF document
      const pdfDoc = new PDFDocument({ margin: 50 });

      // Set response headers for PDF
      res.setHeader("Content-Type", "application/pdf");
      res.setHeader(
        "Content-Disposition",
        `attachment; filename="${pageHeader.replace(/\s+/g, "-").toLowerCase()}-salary-data.pdf"`,
      );

      // Handle PDF completion with a promise to avoid hanging response
      const streamCompletion = new Promise<void>((resolve, reject) => {
        // Handle stream errors
        pdfDoc.on("error", (err) => {
          console.error("PDF generation error:", err);
          reject(err);
        });

        // Handle stream completion
        pdfDoc.on("end", () => {
          resolve();
        });

        // Pipe the PDF directly to the response
        pdfDoc.pipe(res);
      });

      // Add title
      pdfDoc.fontSize(24).text(pageHeader, { align: "center" });

      // Add date
      pdfDoc
        .fontSize(10)
        .text(`Generated on ${new Date().toLocaleDateString()}`, { align: "center" })
        .moveDown(2);

      // Add salary range information
      pdfDoc.fontSize(18).text("Salary Range", { align: "left" }).moveDown(0.5);

      // Display 50th percentile range
      const percentile50 = stats.percentiles["50"];
      if (percentile50) {
        const range50 = formatRange(percentile50.salaryLow, percentile50.salaryHigh, {
          round: true,
        });
        pdfDoc
          .fontSize(14)
          .text(`Median (50th percentile): ${range50}`, { align: "left" })
          .moveDown(0.5);
      }

      // Display 25th percentile range
      const percentile25 = stats.percentiles["25"];
      if (percentile25) {
        const range25 = formatRange(percentile25.salaryLow, percentile25.salaryHigh, {
          round: true,
        });
        pdfDoc.fontSize(14).text(`25th percentile: ${range25}`, { align: "left" }).moveDown(0.5);
      }

      // Display 75th percentile range
      const percentile75 = stats.percentiles["75"];
      if (percentile75) {
        const range75 = formatRange(percentile75.salaryLow, percentile75.salaryHigh, {
          round: true,
        });
        pdfDoc.fontSize(14).text(`75th percentile: ${range75}`, { align: "left" }).moveDown(0.5);
      }

      // Add sample size
      pdfDoc
        .fontSize(12)
        .text(`Based on ${stats.numUniqueRanges.toLocaleString("en-US")} data points`)
        .moveDown(2);

      // Add data points table header
      if (results.length > 0) {
        pdfDoc.fontSize(18).text("Data Points", { align: "left" }).moveDown(0.5);

        const tableTop = pdfDoc.y;
        const companyCol = 50;
        const locationCol = 200;
        const rangeCol = 350;

        pdfDoc
          .font("Helvetica-Bold")
          .text("Company", companyCol, tableTop)
          .text("Location", locationCol, tableTop)
          .text("Salary Range", rangeCol, tableTop)
          .moveDown();

        // Add data rows (limit to first 20 for PDF)
        pdfDoc.font("Helvetica");
        const maxResults = Math.min(results.length, 20);

        for (let i = 0; i < maxResults; i++) {
          const result = results[i];
          const y = pdfDoc.y;

          pdfDoc.text(result.companyName || "Unknown", companyCol, y);

          // Get location from the first post if available
          const location = result.posts[0]?.geonameMatch || "Remote";
          pdfDoc.text(location, locationCol, y);

          const range = formatRange(result.salaryLow, result.salaryHigh);
          pdfDoc.text(range, rangeCol, y);

          pdfDoc.moveDown();

          // Add a new page if we're getting close to the bottom
          if (pdfDoc.y > 700) {
            pdfDoc.addPage();
          }
        }

        // If we truncated results, indicate that
        if (results.length > maxResults) {
          pdfDoc
            .moveDown()
            .text(`... and ${results.length - maxResults} more data points`, { align: "center" });
        }
      }

      // If we have family level data, add a level comparison
      if (tableData && tableData.length > 0) {
        // Add a new page for the level comparison
        pdfDoc.addPage();

        pdfDoc.fontSize(18).text("Level Comparison", { align: "left" }).moveDown(0.5);

        const tableTop = pdfDoc.y;
        const levelCol = 50;
        const labelCol = 150;
        const rangeCol = 350;

        pdfDoc
          .font("Helvetica-Bold")
          .text("Level", levelCol, tableTop)
          .text("Description", labelCol, tableTop)
          .text("Salary Range", rangeCol, tableTop)
          .moveDown();

        // Add level data rows
        pdfDoc.font("Helvetica");

        for (const levelData of tableData) {
          const y = pdfDoc.y;

          pdfDoc.text(levelData.level, levelCol, y);

          // Get description from level if available
          const levelDesc = descriptions[levelData.level as keyof typeof descriptions]?.title || "";
          pdfDoc.text(levelDesc, labelCol, y);

          const range = formatRange(
            levelData.rangeLowPercentile50 || 0,
            levelData.rangeHighPercentile50 || 0,
          );
          pdfDoc.text(range, rangeCol, y);

          pdfDoc.moveDown();
        }
      }

      // Finalize the PDF and wait for stream to complete
      pdfDoc.end();

      await streamCompletion;

      // Return empty props (Next.js won't actually use this since we've already sent the response)
      return { props: {} };
    } catch (error) {
      console.error("Error generating PDF:", error);

      // If headers haven't been sent yet, return error page
      if (!res.headersSent) {
        res.statusCode = 500;
        return {
          props: {
            error: "Failed to generate PDF",
          },
        };
      }

      // Otherwise end the request
      res.end();
      return { props: {} };
    }
  }

  return {
    props,
  };
};

export default function TitlePage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [opened, { toggle }] = useDisclosure(props.mode === "title");

  const searchInfo = useSearchRouteInfo();
  const router = useRouter();
  const contentRef = useRef<HTMLDivElement>(null);
  const levelDescription = descriptions[searchInfo.query.level?.[0] as keyof typeof descriptions];

  function getPageMeta(): { title: string; description: string } {
    const salaryLow = props.stats?.percentiles["50"]?.salaryLow;
    const salaryHigh = props.stats?.percentiles["50"]?.salaryHigh;
    const range = formatRange(salaryLow, salaryHigh, { round: true });

    const currentMonth = new Date().toLocaleString("default", { month: "short" });
    const currentYear = new Date().getFullYear();

    if (props.mode === "family") {
      const level = searchInfo.query.level?.[0];
      const levelDescription = descriptions[level as keyof typeof descriptions];
      const jobFamily = searchInfo.query.jobFamily[0];

      const title = `${jobFamily} Salary - ${level} (${levelDescription?.title || ""}) | ${range}`;
      const description = `The average ${jobFamily} salary range disclosed in job posts is ${range} for ${level} (as of ${currentMonth} ${currentYear}).`;

      return {
        title,
        description,
      };
    } else {
      const jobTitle = searchInfo.query.title[0];

      const title = `${jobTitle} Salary | ${range}`;
      const description = `The average ${jobTitle} salary range disclosed in job posts is ${range} (as of ${currentMonth} ${currentYear}).`;

      return {
        title,
        description,
      };
    }
  }

  const pageMeta = getPageMeta();

  // If we received an error or empty props (after PDF generation), handle it appropriately
  if (!props.title) {
    if (props.error) {
      return (
        <Box p="xl" ta="center">
          <Title order={2} mb="md">
            Error Generating PDF
          </Title>
          <Text>{props.error}</Text>
          <Button mt="xl" component="a" href={router.asPath.replace(/[?&]format=pdf/, "")}>
            Back to Salary Data
          </Button>
        </Box>
      );
    }
    return null;
  }

  return (
    <SelectedPercentileProvider>
      <Head>
        <title>{pageMeta.title}</title>
        <meta name="description" content={pageMeta.description} />
        <meta name="twitter:description" content={pageMeta.description} />
      </Head>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <SearchSidebar key={props.title} />
        <Stack sx={{ flex: 1 }} w="100%" ref={contentRef}>
          <Group
            bg="white"
            w="100%"
            justify="center"
            sx={{
              borderTop: `1px solid var(--mantine-color-gray-3)`,
              borderBottom: `1px solid var(--mantine-color-gray-3)`,
            }}
          >
            <SearchByBar />
          </Group>

          <PageContentBox py="24px" overrideMaxWidth={PAGE_CONTENT_MAX_WIDTH}>
            <Stack align="center" gap={16}>
              {searchInfo.searchMode === "title" ? (
                <Title order={2} ta="center">
                  {searchInfo.query.title}
                </Title>
              ) : (
                <Stack>
                  <Title order={2} ta="center">
                    {searchInfo.query.jobFamily}
                  </Title>
                  <Group align="center" justify="center" gap={4}>
                    <Title order={3} ta="center" c="gray.5">
                      {/*
                       // @ts-expect-error TS2345 */}
                      {levelLabel(searchInfo.query.level)}
                    </Title>
                    {levelDescription && (
                      <Tooltip label={levelDescription.description}>
                        <IconInfoCircle size={18} color="var(--mantine-color-gray-5)" />
                      </Tooltip>
                    )}
                  </Group>
                </Stack>
              )}
            </Stack>
          </PageContentBox>

          <PageContentBox overrideMaxWidth={PAGE_CONTENT_MAX_WIDTH}>
            <Card radius={12}>
              <Card.Section withBorder px={16} py={16}>
                <Group justify="space-between">
                  <Text fw={600} fz={18}>
                    Salary Range
                  </Text>
                  <PercentileSelector suppressLabel />
                </Group>
              </Card.Section>
              <Card.Section>
                <Box>
                  {props.percentiles && <MarketRangeCard percentiles={props.percentiles} />}
                </Box>
                {props.mode === "title" &&
                  props.numTotalResults &&
                  props.numTotalResults > 0 &&
                  props.numUniqueRanges && (
                    <Stack gap={2} align="center" mb={8}>
                      <SampleSizeBadge size="md" withSuffix sampleSize={props.numUniqueRanges} />
                      <Text fz="xs" c="gray.6">{`${props.numUniqueRanges.toLocaleString(
                        "en-US",
                      )} Data Points`}</Text>
                    </Stack>
                  )}
              </Card.Section>

              <Card.Section withBorder>
                <UnstyledButton onClick={toggle} w="100%" ta="center">
                  <Group justify="center" mr={12} bg="gray.1" w="100%">
                    <Text fz={16} py={8} c="brand.6" fw={500}>
                      {opened ? "Hide Data Points" : "View Data Points"}
                    </Text>
                    <Box
                      sx={{
                        transform: opened ? "rotate(0deg)" : "rotate(180deg)",
                        transition: "all 0.1s ease-in-out 0s",
                        marginTop: opened ? 4 : 0,
                        marginBottom: opened ? 0 : 4,
                      }}
                    >
                      <IconChevronUp size={16} color="var(--mantine-color-brand-6)" />
                    </Box>
                  </Group>
                </UnstyledButton>

                <Collapse in={opened}>
                  {props.results && (
                    <TitlePageResultsCard
                      results={props.results}
                      offset={props.offset || 0}
                      numResultsOnPage={props.results.length}
                      numTotalResults={props.numTotalResults || 0}
                    />
                  )}
                </Collapse>
              </Card.Section>
            </Card>
          </PageContentBox>

          {props.familyLevelData && (
            <PageContentBox py="24px" overrideMaxWidth={PAGE_CONTENT_MAX_WIDTH}>
              <Card radius={12}>
                <Card.Section withBorder px={16} py={16}>
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Text fw={600} fz={18}>
                        Level Comparison
                      </Text>
                    </Group>
                    <PercentileSelector suppressLabel />
                  </Group>
                </Card.Section>
                <Card.Section>
                  <ScrollArea type="auto">
                    <LevelComparisonTable data={props.familyLevelData} />
                  </ScrollArea>
                </Card.Section>
              </Card>
            </PageContentBox>
          )}

          <Box mt={{ base: 48, sm: 120 }}></Box>
        </Stack>
      </Box>
    </SelectedPercentileProvider>
  );
}

TitlePage.getLayout = getFullWidthLayout;

{
  /* <JobTitleSearchBar />
            <JobTitleCard
                dataComparison={props.dataComparison}
                jobTitle={props.title}
                stats={props.titleCardStats}
                similarTitles={props.similarTitles}
              /> */
}
